import { apiFetchUrl, apiJson, ApiError, API_BASE } from './client';
import type { ExportFormat } from '@/lib/export';

/**
 * Server-side export.
 *
 * The gateway re-runs the query under `COPY … TO` on the principal's duckling
 * and writes straight to a staging object store, so an export can be far larger
 * than anything the browser cached. That is exactly the case the client-side
 * path cannot serve.
 *
 * Cached results still export locally: it is faster and needs no round trip.
 * The rule is only ever "cached → local, not cached → server".
 */

export interface ExportTicket {
  /** Relative, therefore same-origin: the normal bearer header applies. */
  readonly downloadUrl: string;
  readonly format: ExportFormat;
  readonly sizeBytes: number;
  readonly expiresAt: string;
}

export class ExportExpiredError extends Error {
  constructor() {
    super('This download link has expired. Run the export again.');
    this.name = 'ExportExpiredError';
  }
}

export function requestExport(
  queryId: string,
  format: ExportFormat,
  signal?: AbortSignal,
): Promise<ExportTicket> {
  const init: RequestInit = { method: 'POST' };
  if (signal) init.signal = signal;
  return apiJson<ExportTicket>(
    `/files/exports/${encodeURIComponent(queryId)}?format=${encodeURIComponent(format)}`,
    init,
  );
}

/**
 * Resolves the server's `downloadUrl` against our own origin.
 *
 * It is handed to us relative; treating it as an external link would drop the
 * auth header and break the download.
 */
export function resolveDownloadUrl(downloadUrl: string): string {
  if (downloadUrl.startsWith('/')) return downloadUrl;
  // Defensive: a bare id or relative segment still belongs under the API base.
  return `${API_BASE}/${downloadUrl.replace(/^\.?\//, '')}`;
}

export async function fetchExport(
  downloadUrl: string,
  signal?: AbortSignal,
): Promise<Blob> {
  const init: RequestInit = {};
  if (signal) init.signal = signal;

  try {
    const response = await apiFetchUrl(resolveDownloadUrl(downloadUrl), init);
    return await response.blob();
  } catch (cause) {
    // Links are registered in memory with a TTL and do not survive a gateway
    // restart, so a 404 here means "expired", not "missing feature".
    if (cause instanceof ApiError && cause.status === 404) throw new ExportExpiredError();
    throw cause;
  }
}
